import React, { useState, useEffect } from 'react';
import { monthlyOverviewAPI, timeOffAPI } from '../../services/api';
import { startOfISOWeek, addWeeks } from 'date-fns';
import './MonthlyOverview.css';
import { 
  Calendar, 
  BarChart3, 
  Briefcase, 
  Palmtree, 
  Heart, 
  Gift, 
  Clock, 
  Home, 
  Info,
  CheckCircle,
  Repeat,
  SmartphoneNfc,
  Edit,
  Laptop
} from 'lucide-react';

interface DayData {
  date: string;
  dayOfWeek: number;
  isWorkday: boolean;
  isHoliday: boolean;
  isToday: boolean;
  workLogs: Array<{
    id: number;
    startTime: string;
    endTime: string | null;
    totalMinutes: number | null;
    description: string | null;
    logMethod: string;
    logMethod2?: string; // Added for clock-out method
  }>;
  timeOff: {
    type: 'VACATION' | 'SICK' | 'SPECIAL_LEAVE';
    reason: string | null;
  } | null;
  timeOffEntries?: Array<{
    id: number;
    type: 'VACATION' | 'SICK' | 'SPECIAL_LEAVE';
    reason: string | null;
    startDate: string;
    endDate: string;
  }>;
  holiday: {
    name: string;
    description: string | null;
  } | null;
  actualMinutes: number;
  expectedMinutes: number;
  status: 'workday' | 'weekend' | 'holiday' | 'vacation' | 'sick' | 'current' | 'overtime';
}

interface MonthlyData {
  period: {
    startDate: string;
    endDate: string;
    type: 'week' | 'month';
    year: number;
    month: number;
    week: number | null;
  };
  user: {
    id?: number;
    firstName: string;
    lastName: string;
    weeklyHours: number;
    workDays: string;
    dailyExpectedMinutes: number;
    startDate: string;
    initialOvertimeMinutes?: number;
  };
  summary: {
    totalActualMinutes: number;
    totalExpectedMinutes: number;
    overtimeMinutes: number;
    totalActualMinutesUpToToday: number;
    totalExpectedMinutesUpToToday: number;
    overtimeMinutesUpToToday: number;
    isCurrentPeriod: boolean;
    workdayMinutes: number;
    overtimeFromNonWorkdays: number;
    holidayMinutes: number;
    vacationMinutes: number;
    sickMinutes: number;
    totalDays: number;
    workdays: number;
    holidays: number;
    vacations: number;
    sickDays: number;
    cumulativeOvertimeFromPrevious: number;
    totalCumulativeOvertime: number;
  };
  days: DayData[];
}

export const MonthlyOverview: React.FC = () => {
  // Statische Variable um die Anzeige der Log-Methoden zu steuern
  const SHOW_LOG_METHODS = false; // Auf false setzen um Log-Methoden zu verstecken

  const [viewType, setViewType] = useState<'month' | 'week'>('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MonthlyData | null>(null);
  const [availablePeriods, setAvailablePeriods] = useState<{
    availableMonths: Array<{ year: number; month: number; label: string }>;
    availableYears: number[];
    userStartDate: string;
  } | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayData | null>(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [timeOffEntries, setTimeOffEntries] = useState<Array<{
    id: number;
    startDate: string;
    endDate: string;
    type: 'VACATION' | 'SICK' | 'SPECIAL_LEAVE';
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    reason: string | null;
    workingDays: number;
    createdAt: string;
  }>>([]);

  useEffect(() => {
    loadAvailablePeriods();
  }, []);

  useEffect(() => {
    if (availablePeriods) {
      loadData();
      loadTimeOffEntries();
    }
  }, [viewType, selectedYear, selectedMonth, selectedWeek, availablePeriods]);

  const getCurrentWeekNumber = () => {
    const now = new Date();

    const thursday = new Date(now.getTime() + (3 - ((now.getDay() + 6) % 7)) * 86400000);
    const year = thursday.getFullYear();
    const firstThursday = new Date(year, 0, 4);
    const weekNum = 1 + Math.round(((thursday.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
    
    return Math.max(1, Math.min(weekNum, getWeeksInYear(year)));
  };

  useEffect(() => {
    if (viewType === 'week') {
      const currentWeek = getCurrentWeekNumber();
      const currentYear = new Date().getFullYear();
      
      setSelectedYear(currentYear);
      setSelectedWeek(currentWeek);
    }
  }, [viewType]);

  const loadAvailablePeriods = async () => {
    try {
      const response = await monthlyOverviewAPI.getAvailablePeriods();
      setAvailablePeriods(response.data);

      if (viewType === 'week') {
        const currentWeek = getCurrentWeekNumber();
        setSelectedWeek(currentWeek);
      }
    } catch (error) {
      console.error('Error loading available periods:', error);
    }
  };

  const calculateWeekDates = (year: number, week: number) => {
    const firstWeek = startOfISOWeek(new Date(year, 0, 4)); // 4. Januar ist immer in KW 1
    const targetWeek = addWeeks(firstWeek, week - 1);
    
    const startDate = startOfISOWeek(targetWeek);
    startDate.setDate(startDate.getDate() +1); //Damit Montag der erste Tag der Woche ist
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    return { startDate, endDate };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      let params: any;
      
      if (viewType === 'week') {
        const { startDate, endDate } = calculateWeekDates(selectedYear, selectedWeek);
        
        params = {
          year: selectedYear,
          week: selectedWeek,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        };
      } else {
        params = { year: selectedYear, month: selectedMonth };
      }

      console.log('🔄 Loading monthly overview with params:', params);
      
      const response = await monthlyOverviewAPI.getMonthlyOverview(params);
      
      console.log('Monthly overview response:', response.data);
      console.log('Days with work logs:', response.data.days.filter((d: any) => d.workLogs.length > 0));
      
      setData(response.data);
    } catch (error) {
      console.error('Error loading monthly overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTimeOffEntries = async () => {
    try {
      const response = await timeOffAPI.getTimeOffRequests({
        limit: 100
      });
      setTimeOffEntries(response.data.timeOffs);
    } catch (error) {
      console.error('Error loading time off entries:', error);
    }
  };

  const formatMinutes = (minutes: number) => {
    if (minutes === 0) return '0:00';
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes < 0 ? '-' : '';
    return `${sign}${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const getDayStatusClass = (day: DayData) => {
    const baseClass = 'calendar-day';
    
    if (day.isToday) return `${baseClass} current-day`;
    if (day.status === 'sick') return `${baseClass} sick-day`;
    if (day.status === 'vacation') return `${baseClass} vacation-day`;
    if (day.status === 'holiday') return `${baseClass} holiday-day`;
    if (day.status === 'workday') return `${baseClass} workday`;
    if (day.status === 'weekend') return `${baseClass} weekend-day`;
    if (day.status === 'overtime') return `${baseClass} overtime-day`;
    
    return baseClass;
  };

  const getDayLabel = (day: DayData) => {
    const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    return dayNames[day.dayOfWeek - 1];
  };

  const getWeeksInYear = (year: number) => {
    // ISO 8601
    const jan1 = new Date(year, 0, 1);
    const dec31 = new Date(year, 11, 31);
    const dec31WeekDay = dec31.getDay();
    const dec31DaysFromMonday = dec31WeekDay === 0 ? 6 : dec31WeekDay - 1;
    const dec31Monday = new Date(dec31);
    dec31Monday.setDate(dec31.getDate() - dec31DaysFromMonday);
    
    if (dec31Monday.getFullYear() > year) {
      return 52;
    }
    
    const jan1WeekDay = jan1.getDay();
    if (jan1WeekDay >= 4 || jan1WeekDay === 0) {
      return 53;
    }
    
    return 52;
  };

  const getWeekDateRange = (year: number, week: number) => {
    const { startDate, endDate } = calculateWeekDates(year, week);
    const adjustedStartDate = new Date(startDate);
    adjustedStartDate.setDate(startDate.getDate()-1);
    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setDate(endDate.getDate()-1);

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('de-DE', { 
        day: '2-digit', 
        month: '2-digit' 
      });
    };
    return `${formatDate(adjustedStartDate)}-${formatDate(adjustedEndDate)}`;
  };

  const getAvailableMonthsForYear = () => {
    if (!availablePeriods) return [];
    return availablePeriods.availableMonths.filter(m => m.year === selectedYear);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (!availablePeriods) return;
    
    const currentMonthIndex = availablePeriods.availableMonths.findIndex(
      m => m.year === selectedYear && m.month === selectedMonth
    );
    
    if (currentMonthIndex === -1) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = Math.max(0, currentMonthIndex - 1);
    } else {
      newIndex = Math.min(availablePeriods.availableMonths.length - 1, currentMonthIndex + 1);
    }
    
    const newMonth = availablePeriods.availableMonths[newIndex];
    setSelectedYear(newMonth.year);
    setSelectedMonth(newMonth.month);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const weeksInCurrentYear = getWeeksInYear(selectedYear);
    
    if (direction === 'prev') {
      if (selectedWeek > 1) {
        setSelectedWeek(selectedWeek - 1);
      } else {
        const prevYear = selectedYear - 1;
        if (availablePeriods?.availableYears.includes(prevYear)) {
          setSelectedYear(prevYear);
          setSelectedWeek(getWeeksInYear(prevYear));
        }
      }
    } else {
      if (selectedWeek < weeksInCurrentYear) {
        setSelectedWeek(selectedWeek + 1);
      } else {
        const nextYear = selectedYear + 1;
        if (availablePeriods?.availableYears.includes(nextYear)) {
          setSelectedYear(nextYear);
          setSelectedWeek(1);
        }
      }
    }
  };

  const openDayModal = (day: DayData) => {
    setSelectedDay(day);
    setShowDayModal(true);
  };

  const closeDayModal = () => {
    setShowDayModal(false);
    setSelectedDay(null);
  };

  const formatWorkDays = (workDays: string) => {
    if (!workDays) return 'Mo-Fr';
    
    const dayMapping: { [key: string]: string } = {
      '1': 'Mo',
      '2': 'Di', 
      '3': 'Mi',
      '4': 'Do',
      '5': 'Fr',
      '6': 'Sa',
      '7': 'So'
    };
    
    const days = workDays.split(',').map(day => dayMapping[day.trim()]).filter(Boolean);
    return days.join(', ');
  };

  const getTimeOffEntriesForDay = (date: string) => {
    return timeOffEntries.filter(entry => {
      const entryStart = new Date(entry.startDate);
      const entryEnd = new Date(entry.endDate);
      const dayDate = new Date(date);
      
      return dayDate >= entryStart && dayDate <= entryEnd && entry.status === 'APPROVED';
    });
  };

  // Helper function to calculate actual break time from database
  const getActualBreakMinutes = (workLog: any) => {
    // Use breakMinutes from database if available, otherwise return 0
    return workLog.breakMinutes || 0;
  };

  const getTimeOffIcon = (type: string) => {
    switch (type) {
      case 'SICK': return <Heart size={16} style={{ color: '#dc3545' }} />;
      case 'VACATION': return <Palmtree size={16} style={{ color: '#10b981' }} />;
      case 'SPECIAL_LEAVE': return <Gift size={16} style={{ color: '#f59e0b' }} />;
      default: return <Info size={16} style={{ color: '#6b7280' }} />;
    }
  };

  const getTimeOffLabel = (type: string, reason?: string | null) => {
    const defaultLabels = {
      'SICK': 'Krank',
      'VACATION': 'Urlaub',
      'SPECIAL_LEAVE': 'Sonderurlaub'
    };
    return reason || defaultLabels[type as keyof typeof defaultLabels] || type;
  };

  if (loading && !data) {
    return <div className="loading">Lade Monatsübersicht...</div>;
  }

  return (
    <div className="monthly-overview">
      <div className="overview-header">
        <h2>
          <Calendar size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Monatsübersicht
        </h2>
        
        {/* View Type Selector */}
        <div className="view-selector">
          <button
            className={`view-btn ${viewType === 'month' ? 'active' : ''}`}
            onClick={() => setViewType('month')}
          >
            <Calendar size={16} style={{ marginRight: '4px' }} />
            Monat
          </button>
          <button
            className={`view-btn ${viewType === 'week' ? 'active' : ''}`}
            onClick={() => {
              setViewType('week');
            }}
          >
            <BarChart3 size={16} style={{ marginRight: '4px' }} />
            Woche
          </button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="period-selector">
        <div className="selector-group">
          <label>Jahr:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {availablePeriods?.availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {viewType === 'month' ? (
          <div className="selector-group">
            <label>Monat:</label>
            <div className="selector-with-navigation">
              <button 
                className="nav-arrow" 
                onClick={() => navigateMonth('prev')}
                disabled={!availablePeriods || availablePeriods.availableMonths.findIndex(m => m.year === selectedYear && m.month === selectedMonth) === 0}
                title="Vorheriger Monat"
              >
                ←
              </button>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {getAvailableMonthsForYear().map(month => (
                  <option key={`${month.year}-${month.month}`} value={month.month}>
                    {month.label.split(' ')[0]}
                  </option>
                ))}
              </select>
              <button 
                className="nav-arrow" 
                onClick={() => navigateMonth('next')}
                disabled={!availablePeriods || availablePeriods.availableMonths.findIndex(m => m.year === selectedYear && m.month === selectedMonth) === availablePeriods.availableMonths.length - 1}
                title="Nächster Monat"
              >
                →
              </button>
            </div>
          </div>
        ) : (
          <div className="selector-group">
            <label>Woche:</label>
            <div className="selector-with-navigation">
              <button 
                className="nav-arrow" 
                onClick={() => navigateWeek('prev')}
                disabled={!availablePeriods || (selectedWeek === 1 && !availablePeriods.availableYears.includes(selectedYear - 1))}
                title="Vorherige Woche"
              >
                ←
              </button>
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
              >
                {Array.from({ length: getWeeksInYear(selectedYear) }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    KW {i + 1} ({getWeekDateRange(selectedYear, i + 1)})
                  </option>
                ))}
              </select>
              <button 
                className="nav-arrow" 
                onClick={() => navigateWeek('next')}
                disabled={!availablePeriods || (selectedWeek === getWeeksInYear(selectedYear) && !availablePeriods.availableYears.includes(selectedYear + 1))}
                title="Nächste Woche"
              >
                →
              </button>
            </div>
          </div>
        )}

        <button className="btn secondary" onClick={loadData} disabled={loading}>
          {loading ? 'Lädt...' : <><Repeat size={16} /> Aktualisieren</>}
        </button>
      </div>

      {data && (
        <>
          {/* User Info Display */}
          {data.user && (
            <div className="user-info-summary">
              <p>
                <strong>{data.user.firstName} {data.user.lastName}</strong> - 
                {data.user.weeklyHours}h/Woche - 
                Arbeitstage: {formatWorkDays(data.user.workDays)}
              </p>
            </div>
          )}

          {/* Summary Cards */}
          <div className="summary-section">
            <div className="summary-cards">
              <div className="summary-card total">
                <h4>Gesamtzeit</h4>
                <p className="big-number">{formatMinutes(data.summary.totalActualMinutes)}</p>
                <small>Ist-Zeit (gesamt)</small>
              </div>
              <div className="summary-card expected">
                <h4>Soll-Zeit (Periode)</h4>
                <p className="big-number">{formatMinutes(data.summary.totalExpectedMinutes)}</p>
                <small>Erwartet (gesamt)</small>
              </div>
              <div className={`summary-card ${data.summary.overtimeMinutes >= 0 ? 'positive' : 'negative'}`}>
                <h4>Differenz (Periode)</h4>
                <p className="big-number">{formatMinutes(data.summary.overtimeMinutes)}</p>
                <small>{data.summary.overtimeMinutes >= 0 ? 'Überstunden' : 'Unterstunden'} (gesamt)</small>
              </div>
              {data.summary.isCurrentPeriod && (
                <>
                  <div className="summary-card expected">
                    <h4>Soll-Zeit bis heute</h4>
                    <p className="big-number">{formatMinutes(data.summary.totalExpectedMinutesUpToToday)}</p>
                    <small>Erwartet bis {new Date().toLocaleDateString('de-DE')}</small>
                  </div>
                  <div className={`summary-card ${data.summary.overtimeMinutesUpToToday >= 0 ? 'positive' : 'negative'}`}>
                    <h4>Differenz bis heute</h4>
                    <p className="big-number">{formatMinutes(data.summary.overtimeMinutesUpToToday)}</p>
                    <small>{data.summary.overtimeMinutesUpToToday >= 0 ? 'Überstunden' : 'Unterstunden'} bis heute</small>
                  </div>
                </>
              )}
              <div className={`summary-card ${data.summary.cumulativeOvertimeFromPrevious >= 0 ? 'positive' : 'negative'}`}>
                <h4>Saldo Vorperioden</h4>
                <p className="big-number">{formatMinutes(data.summary.cumulativeOvertimeFromPrevious)}</p>
                <small>Übertrag seit {new Date(data.user.startDate).toLocaleDateString('de-DE')}</small>
              </div>
              <div className={`summary-card ${data.summary.totalCumulativeOvertime >= 0 ? 'positive' : 'negative'}`}>
                <h4>Gesamtsaldo</h4>
                <p className="big-number">{formatMinutes(data.summary.totalCumulativeOvertime)}</p>
                <small>
                  {data.summary.cumulativeOvertimeFromPrevious !== 0 && (
                    <>
                      {formatMinutes(data.summary.cumulativeOvertimeFromPrevious)} + {formatMinutes(data.summary.overtimeMinutes)}
                      <br />
                    </>
                  )}
                  Kumuliert seit {new Date(data.user.startDate).toLocaleDateString('de-DE')}
                </small>
              </div>
            </div>

            {/* Day Statistics */}
            <div className="breakdown-cards">
              <div className="breakdown-card">
                <h5>
                  <BarChart3 size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Gesamt Tage
                </h5>
                <span>{data.summary.totalDays}</span>
              </div>
              <div className="breakdown-card">
                <h5>
                  <Briefcase size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Arbeitstage
                </h5>
                <span>{data.summary.workdays}</span>
              </div>
              <div className="breakdown-card">
                <h5>
                  <Palmtree size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Urlaubstage
                </h5>
                <span>{data.summary.vacations}</span>
              </div>
              <div className="breakdown-card">
                <h5>
                  <Heart size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Kranktage
                </h5>
                <span>{data.summary.sickDays}</span>
              </div>
              <div className="breakdown-card">
                <h5>
                  <Gift size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Feiertage
                </h5>
                <span>{data.summary.holidays}</span>
              </div>
            </div>

            {/* Detailed breakdown */}
            <div className="breakdown-cards">
              <div className="breakdown-card">
                <h5>Reguläre Arbeitszeit</h5>
                <span>{formatMinutes(data.summary.workdayMinutes)}</span>
              </div>
              <div className="breakdown-card">
                <h5>Gesamt-Überstunden (nicht reguläre Zeit)</h5>
                <span>{formatMinutes(data.summary.overtimeFromNonWorkdays)}</span>
              </div>
              <div className="breakdown-card">
                <h5>Urlaub</h5>
                <span>{formatMinutes(data.summary.vacationMinutes)}</span>
              </div>
              <div className="breakdown-card">
                <h5>Kranktage</h5>
                <span>{formatMinutes(data.summary.sickMinutes)}</span>
              </div>
              <div className="breakdown-card">
                <h5>Feiertage</h5>
                <span>{formatMinutes(data.summary.holidayMinutes)}</span>
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div className="calendar-section">
            <div className={`calendar ${viewType}`}>
              {/* Header for both month and week view */}
              <div className="calendar-header">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                  <div key={day} className="calendar-header-day">{day}</div>
                ))}
              </div>
              
              <div className="calendar-grid">
                {viewType === 'month' ? (
                  (() => {
                    if (!data) return null;
                    
                    const firstDayOfMonth = new Date(data.period.year, data.period.month - 1, 1);
                    const lastDayOfMonth = new Date(data.period.year, data.period.month, 0);
                    const firstDayWeekday = firstDayOfMonth.getDay();
                    const normalizedFirstDay = firstDayWeekday === 0 ? 7 : firstDayWeekday;
                    const mondayOffset = normalizedFirstDay - 1;
                    const calendarCells = [];
                    
                    for (let i = 0; i < mondayOffset; i++) {
                      calendarCells.push(
                        <div key={`empty-${i}`} className="calendar-day empty-day">
                        </div>
                      );
                    }
                    
                    for (let dayNum = 1; dayNum <= lastDayOfMonth.getDate(); dayNum++) {
                      const year = data.period.year;
                      const month = data.period.month;
                      const dateStr = `${year}-${month.toString().padStart(2, '0')}-${dayNum.toString().padStart(2, '0')}`;
                      const dayData = data.days.find(d => d.date === dateStr);
                      
                      if (dayData) {
                        calendarCells.push(
                          <div 
                            key={dayData.date} 
                            className={`${getDayStatusClass(dayData)} clickable-view-only`}
                            onClick={() => openDayModal(dayData)}
                            title="Klicken für Details"
                          >

                            <div className="day-header">
                              <span className="day-number">{dayNum}</span>
                              <span className="day-name">{getDayLabel(dayData)}</span>
                            </div>
                            
                            <div className="day-content">
                              {dayData.actualMinutes > 0 && (
                                <div className="work-time">
                                  {formatMinutes(dayData.actualMinutes)}
                                </div>
                              )}
                              
                              {/* NEW: Show daily overtime/undertime */}
                              {dayData.expectedMinutes > 0 && (dayData.actualMinutes !== dayData.expectedMinutes) && (
                                <div className={`day-overtime ${dayData.actualMinutes - dayData.expectedMinutes >= 0 ? 'positive' : 'negative'}`}>
                                  {dayData.actualMinutes - dayData.expectedMinutes >= 0 ? '+' : ''}{formatMinutes(dayData.actualMinutes - dayData.expectedMinutes)}
                                </div>
                              )}
                              
                              {dayData.holiday && (
                                <div className="day-info holiday">
                                  <Gift size={16} style={{ marginRight: '4px' }} />
                                  {dayData.holiday.name}
                                </div>
                              )}
                              
                              {(() => {
                                const dayTimeOffEntries = getTimeOffEntriesForDay(dayData.date);
                                
                                if (dayTimeOffEntries.length === 0 && dayData.timeOff) {
                                  return (
                                    <div className={`day-info timeoff ${dayData.timeOff.type.toLowerCase()}`}>
                                      {getTimeOffIcon(dayData.timeOff.type)} {getTimeOffLabel(dayData.timeOff.type, dayData.timeOff.reason)}
                                    </div>
                                  );
                                }

                                return dayTimeOffEntries.map((entry, index) => (
                                  <div key={`${entry.id}-${index}`} className={`day-info timeoff ${entry.type.toLowerCase()}`}>
                                    {getTimeOffIcon(entry.type)} {getTimeOffLabel(entry.type, entry.reason)}
                                  </div>
                                ));
                              })()}
                              
                              {dayData.workLogs && dayData.workLogs.length > 0 && (
                                <div className="work-sessions">
                                  {dayData.workLogs.map(log => {
                                    // Use actual break minutes from database instead of calculating difference
                                    const actualBreakMinutes = getActualBreakMinutes(log);
                                    
                                    return (
                                      <div key={log.id} className="work-session">
                                        <small>
                                          {new Date(log.startTime).toLocaleTimeString('de-DE', { 
                                            hour: '2-digit', 
                                            minute: '2-digit' 
                                          })}
                                          {log.endTime && 
                                            ` - ${new Date(log.endTime).toLocaleTimeString('de-DE', { 
                                              hour: '2-digit', 
                                              minute: '2-digit' 
                                            })}`
                                          } Uhr
                                        </small>
                                        {log.endTime && actualBreakMinutes > 0 && (
                                          <small className="break-deduction" title={`Pausenzeit abgezogen: ${actualBreakMinutes} Min`}>
                                            <Clock size={12} style={{ marginRight: '2px' }} />
                                            -{actualBreakMinutes}min
                                          </small>
                                        )}
                                        {log.description && (
                                          <small className="session-desc">{log.description}</small>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      } else {
                        const fallbackDate = new Date(year, month - 1, dayNum);
                        const dayOfWeek = fallbackDate.getDay();
                        const normalizedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
                        const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
                        const dayLabel = dayNames[normalizedDay - 1];
                        
                        calendarCells.push(
                          <div key={dateStr} className="calendar-day no-data">
                            <div className="day-header">
                              <span className="day-number">{dayNum}</span>
                              <span className="day-name">{dayLabel}</span>
                            </div>
                          </div>
                        );
                      }
                    }
                    
                    return calendarCells;
                  })()
                ) : (
                  data.days.map(day => (
                    <div 
                      key={day.date} 
                      className={`${getDayStatusClass(day)} clickable-view-only`}
                      onClick={() => openDayModal(day)}
                      title="Klicken für Details"
                    >

                      <div className="day-header">
                        <span className="day-number">{new Date(day.date).getDate()}</span>
                        <span className="day-name">{getDayLabel(day)}</span>
                      </div>
                      
                      <div className="day-content">
                        {day.actualMinutes > 0 && (
                          <div className="work-time">
                            {formatMinutes(day.actualMinutes)}
                          </div>
                        )}
                        
                        {/* NEW: Show daily overtime/undertime */}
                        {day.expectedMinutes > 0 && (day.actualMinutes !== day.expectedMinutes) && (
                          <div className={`day-overtime ${day.actualMinutes - day.expectedMinutes >= 0 ? 'positive' : 'negative'}`}>
                            {day.actualMinutes - day.expectedMinutes >= 0 ? '+' : ''}{formatMinutes(day.actualMinutes - day.expectedMinutes)}
                          </div>
                        )}
                        
                        {day.holiday && (
                          <div className="day-info holiday">
                            <Gift size={16} style={{ marginRight: '4px' }} />
                            {day.holiday.name}
                          </div>
                        )}
                        
                        {(() => {
                          const dayTimeOffEntries = getTimeOffEntriesForDay(day.date);
                          if (dayTimeOffEntries.length === 0 && day.timeOff) {
                            return (
                              <div className={`day-info timeoff ${day.timeOff.type.toLowerCase()}`}>
                                {getTimeOffIcon(day.timeOff.type)} {getTimeOffLabel(day.timeOff.type, day.timeOff.reason)}
                              </div>
                            );
                          }
                          
                          return dayTimeOffEntries.map((entry, index) => (
                            <div key={`${entry.id}-${index}`} className={`day-info timeoff ${entry.type.toLowerCase()}`}>
                              {getTimeOffIcon(entry.type)} {getTimeOffLabel(entry.type, entry.reason)}
                            </div>
                          ));
                        })()}
                        
                        {day.workLogs && day.workLogs.length > 0 && (
                          <div className="work-sessions">
                            {day.workLogs.map(log => {
                              // Use actual break minutes from database instead of calculating difference
                              const actualBreakMinutes = getActualBreakMinutes(log);
                              
                              return (
                                <div key={log.id} className="work-session">
                                  <small>
                                    {new Date(log.startTime).toLocaleTimeString('de-DE', { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })}
                                    {log.endTime && 
                                      ` - ${new Date(log.endTime).toLocaleTimeString('de-DE', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}`
                                    } Uhr
                                  </small>
                                  {log.endTime && actualBreakMinutes > 0 && (
                                    <small className="break-deduction" title={`Pausenzeit abgezogen: ${actualBreakMinutes} Min`}>
                                      <Clock size={12} style={{ marginRight: '2px' }} />
                                      -{actualBreakMinutes}min
                                    </small>
                                  )}
                                  {SHOW_LOG_METHODS && log.logMethod && (
                                    <div className="method-badges">
                                      <span className={`method-badge ${(log.logMethod || 'WEBSITE').toLowerCase()}`}>
                                        {(log.logMethod || 'WEBSITE') === 'WEBSITE' ? <Laptop /> : (log.logMethod || 'WEBSITE') === 'NFC' ? <SmartphoneNfc size={12} /> : <Edit size={12} />} 
                                        {log.logMethod || 'WEBSITE'}
                                      </span>
                                      {log.endTime && log.logMethod2 && (
                                        <>
                                          <span className="method-arrow">→</span>
                                          <span className={`method-badge ${log.logMethod2.toLowerCase()}`}>
                                            {log.logMethod2 === 'WEBSITE' ? <Laptop /> : log.logMethod2 === 'NFC' ? <SmartphoneNfc size={12} /> : <Edit size={12} />} 
                                            {log.logMethod2}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  )}
                                  {log.description && (
                                    <small className="session-desc">{log.description}</small>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="calendar-legend">
            <h4>Legende:</h4>
            <div className="legend-items">
              <div className="legend-item">
                <span className="legend-color current"></span>
                <span>Heute</span>
              </div>
              <div className="legend-item">
                <span className="legend-color workday"></span>
                <span>Arbeitstag</span>
              </div>
              <div className="legend-item">
                <span className="legend-color weekend"></span>
                <span>Wochenende/Nicht-Arbeitstag</span>
              </div>
              <div className="legend-item">
                <span className="legend-color holiday"></span>
                <span>Feiertag</span>
              </div>
              <div className="legend-item">
                <span className="legend-color vacation"></span>
                <span>Urlaub</span>
              </div>
              <div className="legend-item">
                <span className="legend-color sick"></span>
                <span>Kranktag</span>
              </div>
              <div className="legend-item">
                <span className="legend-color overtime"></span>
                <span>Überstunden</span>
              </div>
            </div>
          </div>

          {/* Day Detail Modal */}
          {showDayModal && selectedDay && (
            <div className="modal-overlay" onClick={closeDayModal}>
              <div className="modal-content day-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    {new Date(selectedDay.date).toLocaleDateString('de-DE', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h3>
                  <button className="modal-close" onClick={closeDayModal}>×</button>
                </div>
                
                <div className="modal-body">
                  <div className="day-status-info">
                    <span className={`status-badge ${selectedDay.status}`}>
                      {selectedDay.status === 'workday' && (
                        <>
                          <Briefcase size={16} style={{ marginRight: '4px' }} />
                          Arbeitstag
                        </>
                      )}
                      {selectedDay.status === 'weekend' && (
                        <>
                          <Home size={16} style={{ marginRight: '4px' }} />
                          Wochenende
                        </>
                      )}
                      {selectedDay.status === 'holiday' && (
                        <>
                          <Gift size={16} style={{ marginRight: '4px' }} />
                          Feiertag
                        </>
                      )}
                      {selectedDay.status === 'vacation' && (
                        <>
                          <Palmtree size={16} style={{ marginRight: '4px' }} />
                          Urlaub
                        </>
                      )}
                      {selectedDay.status === 'sick' && (
                        <>
                          <Heart size={16} style={{ marginRight: '4px' }} />
                          Kranktag
                        </>
                      )}
                      {selectedDay.status === 'current' && (
                        <>
                          <Calendar size={16} style={{ marginRight: '4px' }} />
                          Heute
                        </>
                      )}
                      {selectedDay.status === 'overtime' && (
                        <>
                          <Clock size={16} style={{ marginRight: '4px' }} />
                          Überstunden
                        </>
                      )}
                    </span>
                  </div>

                  {selectedDay.holiday && (
                    <div className="info-section holiday-info">
                      <h4>
                        <Gift size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Feiertag
                      </h4>
                      <p><strong>{selectedDay.holiday.name}</strong></p>
                      {selectedDay.holiday.description && <p>{selectedDay.holiday.description}</p>}
                    </div>
                  )}

                  {(() => {
                    const dayTimeOffEntries = getTimeOffEntriesForDay(selectedDay.date);
                    if (dayTimeOffEntries.length === 0 && selectedDay.timeOff) {
                      return (
                        <div className="info-section timeoff-info">
                          <h4>
                            {getTimeOffIcon(selectedDay.timeOff.type)} {getTimeOffLabel(selectedDay.timeOff.type)}
                          </h4>
                          {selectedDay.timeOff.reason && <p>{selectedDay.timeOff.reason}</p>}
                        </div>
                      );
                    }
                    return dayTimeOffEntries.map((entry, index) => (
                      <div key={`${entry.id}-${index}`} className="info-section timeoff-info">
                        <h4>
                          {getTimeOffIcon(entry.type)} {getTimeOffLabel(entry.type)}
                        </h4>
                        {entry.reason && <p>{entry.reason}</p>}
                        <div className="timeoff-details">
                          <small>
                            {new Date(entry.startDate).toLocaleDateString('de-DE')} - {new Date(entry.endDate).toLocaleDateString('de-DE')}
                          </small>
                        </div>
                      </div>
                    ));
                  })()}

                  <div className="time-summary">
                    <div className="time-info">
                      <h4>
                        <Clock size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Zeiten
                      </h4>
                      <div className="time-details">
                        <div className="time-row">
                          <span>Arbeitszeit:</span>
                          <span className="time-value">{formatMinutes(selectedDay.actualMinutes)}</span>
                        </div>
                        <div className="time-row">
                          <span>Soll-Zeit:</span>
                          <span className="time-value">{formatMinutes(selectedDay.expectedMinutes)}</span>
                        </div>
                        <div className="time-row">
                          <span>Differenz:</span>
                          <span className={`time-value ${selectedDay.actualMinutes - selectedDay.expectedMinutes >= 0 ? 'positive' : 'negative'}`}>
                            {formatMinutes(selectedDay.actualMinutes - selectedDay.expectedMinutes)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedDay.workLogs && selectedDay.workLogs.length > 0 && (
                    <div className="info-section work-logs-detail">
                      <h4>
                        <BarChart3 size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Arbeitszeiten im Detail
                      </h4>
                      <div className="work-logs-list">
                        {selectedDay.workLogs.map(log => {
                          // Use actual break minutes from database instead of calculating difference
                          const loggedMinutes = log.endTime 
                            ? Math.round((new Date(log.endTime).getTime() - new Date(log.startTime).getTime()) / (1000 * 60))
                            : 0;
                          const actualMinutes = log.totalMinutes || 0;
                          const actualBreakMinutes = getActualBreakMinutes(log);
                          
                          return (
                            <div key={log.id} className="work-log-item">
                              <div className="log-time">
                                <strong>
                                  {new Date(log.startTime).toLocaleTimeString('de-DE', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })} Uhr
                                  {log.endTime && 
                                    ` - ${new Date(log.endTime).toLocaleTimeString('de-DE', { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })} Uhr`
                                  }
                                </strong>
                              </div>
                              
                              <div className="log-details">
                                {log.endTime && (
                                  <>
                                    <div className="log-calculation">
                                      <span>Geloggte Zeit: {formatMinutes(loggedMinutes)}</span>
                                      {actualBreakMinutes > 0 && (
                                        <span className="break-info">
                                          Abzug Pausenzeit: -{formatMinutes(actualBreakMinutes)}
                                        </span>
                                      )}
                                      <span className="actual-time">
                                        <strong>Arbeitszeit: {formatMinutes(actualMinutes)}</strong>
                                      </span>
                                    </div>
                                  </>
                                )}
                                
                                {!log.endTime && (
                                  <div className="log-status">
                                    <span className="active-session">
                                      <CheckCircle size={16} style={{ marginRight: '4px', color: '#10b981' }} />
                                      Aktive Sitzung
                                    </span>
                                  </div>
                                )}
                                
                                <div className="log-methods">
                                  {SHOW_LOG_METHODS && (
                                    <div className="method-badges">
                                      <span className={`method-badge ${(log.logMethod || 'WEBSITE').toLowerCase()}`}>
                                        Ein: {(log.logMethod || 'WEBSITE') === 'WEBSITE' ? <Laptop /> : (log.logMethod || 'WEBSITE') === 'NFC' ? <SmartphoneNfc size={12} /> : <Edit size={12} />} 
                                        {log.logMethod || 'WEBSITE'}
                                      </span>
                                      {log.endTime && log.logMethod2 && (
                                        <span className={`method-badge ${log.logMethod2.toLowerCase()}`}>
                                          Aus: {log.logMethod2 === 'WEBSITE' ? <Laptop /> : log.logMethod2 === 'NFC' ? <SmartphoneNfc size={12} /> : <Edit size={12} />} 
                                          {log.logMethod2}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                
                                {log.description && (
                                  <div className="log-description">
                                    <span>Beschreibung: {log.description}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedDay.workLogs.length === 0 && selectedDay.expectedMinutes > 0 && (
                    <div className="info-section no-work">
                      <p>
                        <Info size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                        Keine Arbeitszeiten erfasst
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};